/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";

export const duplicateExcerpt = async (excerptId: string, workspaceId: string) => {
  const { data: src, error: fetchErr } = await supabase
    .from("excerpts")
    .select("*, excerpt_tags(tag_id), excerpt_topics(topic_id)")
    .eq("id", excerptId)
    .single();
  if (fetchErr || !src) throw fetchErr ?? new Error("Excerpt not found");

  // Decrypt the source notes via RPC (workspace-gated, security definer).
  const { data: decrypted } = await (supabase as any).rpc("get_decrypted_excerpts", {
    _workspace_id: workspaceId,
    _ids: [excerptId],
  });
  const sourceNotes = ((decrypted ?? []) as Array<{ id: string; notes: string | null }>)[0]?.notes ?? null;

  const { data: inserted, error: insErr } = await supabase
    .from("excerpts")
    .insert({
      workspace_id: workspaceId,
      body_html: src.body_html,
      body_text: src.body_text,
      library_item_id: src.library_item_id,
      page_start: src.page_start,
      page_end: src.page_end,
      source_url: src.source_url,
    })
    .select("id")
    .single();
  if (insErr || !inserted) throw insErr ?? new Error("Failed to duplicate");

  // Persist notes via the encrypting RPC.
  if (sourceNotes) {
    await (supabase as any).rpc("set_excerpt_notes", {
      _excerpt_id: inserted.id,
      _notes: sourceNotes,
    });
  }

  const tagLinks = ((src as any).excerpt_tags ?? []).map((t: any) => ({
    excerpt_id: inserted.id,
    tag_id: t.tag_id,
  }));
  const topicLinks = ((src as any).excerpt_topics ?? []).map((t: any) => ({
    excerpt_id: inserted.id,
    topic_id: t.topic_id,
  }));
  if (tagLinks.length) await supabase.from("excerpt_tags").insert(tagLinks);
  if (topicLinks.length) await supabase.from("excerpt_topics").insert(topicLinks);

  return inserted.id;
};

const csvEscape = (v: unknown) => {
  if (v === null || v === undefined) return "";
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const exportExcerptsCsv = async (workspaceId: string) => {
  const { data, error } = await supabase
    .from("excerpts")
    .select(
      "id, body_text, page_start, page_end, source_url, created_at, updated_at, excerpt_tags(tags(name)), excerpt_topics(content_framework_topics(label)), library_items(author_text, title, display_title, published_year, library_item_categories(library_categories(name)))"
    )
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false });
  if (error) throw error;

  // Decrypt notes for the entire workspace via RPC and merge by id.
  const { data: decryptedNotes } = await (supabase as any).rpc("get_decrypted_excerpts", {
    _workspace_id: workspaceId,
    _ids: null,
  });
  const notesById = new Map<string, string | null>(
    ((decryptedNotes ?? []) as Array<{ id: string; notes: string | null }>).map((row) => [row.id, row.notes]),
  );

  const headers = [
    "id",
    "body_text",
    "author",
    "publication",
    "publication_year",
    "page_start",
    "page_end",
    "tags",
    "topics",
    "categories",
    "source_url",
    "notes",
    "created_at",
    "updated_at",
  ];
  const rows = (data ?? []).map((r: any) => {
    const inheritedCategories: string[] = (r.library_items?.library_item_categories ?? [])
      .map((lic: any) => lic.library_categories?.name)
      .filter((n: unknown): n is string => Boolean(n));
    return [
      r.id,
      r.body_text,
      r.library_items?.author_text ?? "",
      r.library_items?.display_title ?? r.library_items?.title ?? "",
      r.library_items?.published_year ?? "",
      r.page_start ?? "",
      r.page_end ?? "",
      (r.excerpt_tags ?? []).map((et: any) => et.tags?.name).filter(Boolean).join("; "),
      (r.excerpt_topics ?? []).map((et: any) => et.content_framework_topics?.label).filter(Boolean).join("; "),
      inheritedCategories.join("; "),
      r.source_url ?? "",
      notesById.get(r.id) ?? "",
      r.created_at,
      r.updated_at,
    ];
  });

  const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `excerptus-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return rows.length;
};